import { useDropzone } from 'react-dropzone';
import { useEffect, useRef } from 'react';
// @mui
import { Alert, Fab, Link } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { Iconify } from '../iconify';
// assets
//
//
import RejectionFiles from './errors-rejection-files';
import MultiFilePreview from './preview-multi-file';
import { UploadProps } from './types';

import { CloudUploadIcon } from '../../assets/icons';
import SingleFilePreview from './preview-single-file';
// ----------------------------------------------------------------------

export default function Upload({
  disabled,
  multiple = false,
  isLoading,
  error,
  helperText,
  header,
  subtitle,
  description,
  paste = false,
  //
  value,
  onDelete,
  //
  thumbnail,
  onUpload,
  onRemove,
  onRemoveAll,
  sx,
  ...other
}: UploadProps) {
  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    multiple,
    disabled,
    ...other,
  });

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!paste || disabled) return;

    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const files: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            // Create a new file with a proper name
            const timestamp = new Date().getTime();
            const newFile = new File([file], `pasted-image-${timestamp}.png`, {
              type: file.type,
            });
            files.push(newFile);
          }
        }
      }

      if (files.length > 0 && other.onDrop) {
        e.preventDefault();
        other.onDrop(files, [], e as any);
      }
    };

    document.addEventListener('paste', handlePaste);

    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [paste, disabled, other]);

  const isArray = Array.isArray(value) && multiple;

  const hasFile = !isArray && !!value;

  const hasFiles = isArray && !!value.length;

  const hasError = isDragReject || !!error;

  const renderPlaceholder = (
    <Stack spacing={3} alignItems="center" justifyContent="center" flexWrap="wrap">
      <Stack gap={1} justifyContent="center" alignItems="center">
        <Fab variant="soft" size="small" sx={{ width: '56px', height: '56px' }} id="upload-icon">
          <CloudUploadIcon />
        </Fab>

        {header && (
          <Typography variant="subtitle1" color="text.primary">
            {header}
          </Typography>
        )}

        <Stack alignItems="center">
          <Typography variant="body2" color="text.primary" id="drop-your-file">
            Drop your file here or{' '}
            <Link sx={{ fontWeight: 400 }} id="browse">
              Browse
            </Link>
          </Typography>
          {paste && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              or paste from clipboard ({navigator.platform.toLowerCase().includes('mac') ? '⌘+V' : 'Ctrl+V'})
            </Typography>
          )}
        </Stack>

        {description && (
          <Typography variant="caption" color="text.secondary">
            {description}
          </Typography>
        )}

        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Stack>
    </Stack>
  );

  const renderMultiPreview = hasFiles && (
    <>
      <MultiFilePreview
        files={value}
        thumbnail={thumbnail}
        error={error}
        helperText={helperText}
        onRemove={onRemove}
        sx={{ my: 3 }}
        showPdfIcon={other.isPDF}
        onDelete={onDelete}
      />

      {(onRemoveAll || onUpload) && (
        <Box gap={1.5} display="flex" justifyContent="flex-end">
          {onRemoveAll && (
            <Button color="inherit" variant="outlined" size="small" onClick={onRemoveAll} id="remove-all-button">
              Remove all
            </Button>
          )}

          {onUpload && (
            <Button size="small" variant="contained" onClick={onUpload} startIcon={<Iconify icon="eva:cloud-upload-fill" id="upload-button" />}>
              Upload
            </Button>
          )}
        </Box>
      )}
    </>
  );

  if (hasFile) {
    return (
      <SingleFilePreview
        file={value}
        onDelete={onDelete}
        error={error}
        helperText={helperText}
        showPdfIcon={other.isPDF}
        showPreview={other?.showPreview}
        onPreview={other.onPreview}
      />
    );
  }

  return (
    <Box sx={{ width: 1, position: 'relative', ...sx }}>
      <Box
        {...getRootProps()}
        sx={{
          p: 5,
          outline: 'none',
          borderRadius: 1,
          cursor: 'pointer',
          overflow: 'hidden',
          position: 'relative',
          border: (theme) => `1px dashed ${alpha(theme.palette.grey[500], 0.2)}`,
          transition: (theme) => theme.transitions.create(['opacity', 'padding']),
          '&:hover': {
            opacity: 0.72,
          },
          ...(isDragActive && {
            opacity: 0.72,
          }),
          ...(disabled && {
            opacity: 0.48,
            pointerEvents: 'none',
          }),
          ...(hasError && {
            borderColor: 'error.main',
          }),
        }}
      >
        <input {...getInputProps()} id="input" />

        {hasFile ? <SingleFilePreview file={value} onDelete={onDelete} /> : renderPlaceholder}
      </Box>

      <RejectionFiles fileRejections={fileRejections} />
      {multiple && error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          <Typography variant="caption" color="error.main">
            {helperText}
          </Typography>
        </Alert>
      )}
      {renderMultiPreview}
    </Box>
  );
}
